import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import {
  OrganizationMember,
  OrganizationPendingInvite,
  OrganizationPlanType,
  UserInfo,
} from '@cartwhl/common/types';
import { getStripePortalSession } from '@cartwhl/common/utils/getStripePortalSession';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import DaysAgo from '@cartwhl/ui/atoms/DaysAgo';
import Text from '@cartwhl/ui/base/Text';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';
import HelpIcon from '@cartwhl/ui/icons/HelpIcon';
import MailIcon from '@cartwhl/ui/icons/MailIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';
import Button from '@cartwhl/ui/input/Button';
import EmptyState from '@cartwhl/ui/messaging/EmptyState';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore, userStore } from 'stores/global';

import AddTeamSeatModal from './AddTeamSeatModal';
import AssignOrEditTeamSeatModal from './AssignOrEditTeamSeatModal';
import ProfileSection from './ProfileSection';
import ProfileTabLoadingState from './ProfileTabLoadingState';

function memberSortKey(m: OrganizationMember) {
  const name =
    m.preferredName || `${m.firstName} ${m.lastName}`.trim() || m.email;
  return name.toLowerCase();
}

function memberDisplayName(m: OrganizationMember) {
  return m.preferredName || `${m.firstName} ${m.lastName}`.trim() || m.email;
}

function isAdminRosterMember(m: OrganizationMember) {
  return m.accessLevel === 'EDIT';
}

function isSeatRosterMember(m: OrganizationMember) {
  return !m.isOrgAdminOnly;
}

function seatSectionPlanLabel(planType?: OrganizationPlanType) {
  if (!planType) return 'Team';
  const labels: Partial<Record<OrganizationPlanType, string>> = {
    team_pro: 'Pro',
    team_scale: 'Scale',
    team_standard: 'Standard',
    individual_basic: 'Basic',
    individual_pro: 'Pro',
    platform: 'Platform',
    api_standard: 'API',
    custom: 'Custom',
    legacy_free: 'Legacy',
    legacy_pro: 'Pro',
    legacy_unlimited: 'Unlimited',
  };
  if (labels[planType]) return labels[planType]!;
  const part = planType.split('_').pop() ?? planType;
  return part.charAt(0).toUpperCase() + part.slice(1);
}

function pendingInviteRoleLabel(inv: OrganizationPendingInvite): string {
  if (inv.isOrgAdminOnly) return 'Admin only (no seat)';
  if (inv.requestedAccessLevel === 'EDIT') return 'Admin + member (seat)';
  return 'Member (seat)';
}

export default function MyNewTeam() {
  const {
    organizationInfo,
    organizationMembers,
    organizationPendingInvites,
    loading: userLoading,
    userInfo,
  } = useSnapshot(userStore.state);

  const [openModal, setOpenModal] = useState<
    'addSeat' | 'assignTeamSeat' | 'confirmRemoveMember' | null
  >(null);
  const [selectedMember, setSelectedMember] =
    useState<OrganizationMember | null>(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);

  useEffect(() => {
    const orgId = userInfo.organizationID;
    if (!orgId) return;
    void userStore.fetchOrganization(orgId);
  }, [userInfo.organizationID]);

  useEffect(() => {
    const orgId = userInfo.organizationID;
    if (!orgId || organizationInfo?.accessLevel !== 'EDIT') return;
    void userStore.fetchOrganizationPendingInvites(orgId);
  }, [userInfo.organizationID, organizationInfo?.accessLevel]);

  const hasEditAccess = organizationInfo?.accessLevel === 'EDIT';

  const closeConfirmRemoveMember = () => {
    setRemovingMember(false);
    setOpenModal(null);
    setSelectedMember(null);
  };

  const handleConfirmRemoveMember = async () => {
    if (!selectedMember) return;
    setRemovingMember(true);
    const ok = await userStore.removeUserFromOrganization(
      selectedMember.userID || selectedMember.email,
    );
    setRemovingMember(false);
    if (ok) {
      noticesStore.success('User removed');
      closeConfirmRemoveMember();
    }
  };

  const adminMembers = useMemo(() => {
    const list = (organizationMembers ?? []).filter(isAdminRosterMember);
    list.sort((a, b) => memberSortKey(a).localeCompare(memberSortKey(b)));
    return list;
  }, [organizationMembers]);

  const seatMembers = useMemo(() => {
    const list = (organizationMembers ?? []).filter(isSeatRosterMember);
    list.sort((a, b) => memberSortKey(a).localeCompare(memberSortKey(b)));
    return list;
  }, [organizationMembers]);

  const seatPlanTitle = seatSectionPlanLabel(organizationInfo?.planType);
  const purchasedSeats = organizationInfo?.seatsPurchased;
  const activeSeats = organizationInfo?.activeSeats;

  const unassignedWorkspaceSeats = useMemo(() => {
    if (
      purchasedSeats === undefined ||
      activeSeats === undefined ||
      !hasEditAccess
    ) {
      return 0;
    }
    return Math.max(0, purchasedSeats - activeSeats);
  }, [purchasedSeats, activeSeats, hasEditAccess]);

  async function requestPortalSession() {
    setPortalLoading(true);
    getStripePortalSession(userInfo.stripeCustomerID)
      .then(({ data }) => {
        if (typeof data !== 'undefined') {
          window.location.href = data.url;
        }
        setPortalLoading(false);
      })
      .catch(() => setPortalLoading(false));
  }

  if (!organizationInfo) {
    return null;
  }

  if (userLoading) {
    return (
      <ProfileTabLoadingState maxWidthClassName="max-w-(--breakpoint-lg)" />
    );
  }

  return (
    <div className="overview max-w-(--breakpoint-lg) mx-auto">
      <div className="mt-8">
        <Text as="h2" variant="studio/body/medium/md">
          {organizationInfo.organizationName}
        </Text>
        <Text
          as="p"
          variant="studio/body/sm"
          className="text-tertiary mt-1 font-mono text-xs opacity-70 dark:opacity-60"
        >
          {organizationInfo.organizationID}
        </Text>
      </div>

      <div className="mt-10 space-y-10">
        <TeamRosterCard
          title={`Team admins`}
          members={adminMembers}
          hasEditAccess={!!hasEditAccess}
          ownerUserId={organizationInfo.createdBy}
          currentUserId={userInfo.userID}
          onEditMember={(m) => {
            setSelectedMember(m);
            setOpenModal('assignTeamSeat');
          }}
          onRequestRemoveMember={(m) => {
            setSelectedMember(m);
            setOpenModal('confirmRemoveMember');
          }}
        />

        <TeamRosterCard
          title={`Team ${seatPlanTitle} seats`}
          subtitle={
            purchasedSeats !== undefined && activeSeats !== undefined
              ? `${activeSeats} of ${purchasedSeats} purchased seats in use`
              : undefined
          }
          members={seatMembers}
          hasEditAccess={!!hasEditAccess}
          ownerUserId={organizationInfo.createdBy}
          currentUserId={userInfo.userID}
          onEditMember={(m) => {
            setSelectedMember(m);
            setOpenModal('assignTeamSeat');
          }}
          onRequestRemoveMember={(m) => {
            setSelectedMember(m);
            setOpenModal('confirmRemoveMember');
          }}
          showSeatActions={!!hasEditAccess}
          onAddSeats={() => setOpenModal('addSeat')}
          onManageSeats={requestPortalSession}
          manageSeatsLoading={portalLoading}
          emptyWorkspaceSeatRows={unassignedWorkspaceSeats}
          onAssignEmptyWorkspaceSeat={() => {
            setSelectedMember(null);
            setOpenModal('assignTeamSeat');
          }}
        />
      </div>

      {hasEditAccess && organizationPendingInvites.length > 0 ? (
        <section className="mt-10">
          <Text as="h3" variant="studio/body/medium/md" className="mb-4">
            Pending email invitations
          </Text>
          <div className="border-primary overflow-hidden rounded-lg border">
            <ul className="divide-primary divide-y">
              {organizationPendingInvites.map((inv) => (
                <li key={inv.inviteID}>
                  <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                    <div className="min-w-0">
                      <Text
                        as="span"
                        variant="studio/body/sm"
                        className="font-medium"
                      >
                        {inv.createdForEmail}
                      </Text>
                      <Text
                        as="div"
                        variant="studio/body/sm"
                        className="text-tertiary mt-0.5 text-xs"
                      >
                        {pendingInviteRoleLabel(inv)} · Invited{' '}
                        <DaysAgo date={inv.createdAt} />
                        {inv.inviteExpiresAt ? (
                          <>
                            {' '}
                            · Expires <DaysAgo date={inv.inviteExpiresAt} />
                          </>
                        ) : null}
                      </Text>
                    </div>
                    <div className="flex flex-wrap gap-2 sm:justify-end">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        loading={inviteActionId === `resend-${inv.inviteID}`}
                        disabled={inviteActionId !== null}
                        onClick={() => {
                          setInviteActionId(`resend-${inv.inviteID}`);
                          void (async () => {
                            const orgId = userInfo.organizationID;
                            if (!orgId) return;
                            await userStore.resendOrganizationInvite(
                              orgId,
                              inv.inviteID,
                            );
                            setInviteActionId(null);
                          })();
                        }}
                      >
                        Resend email
                      </Button>
                      <Button
                        type="button"
                        variant="secondary-destructive"
                        size="sm"
                        icon={<TrashIcon />}
                        loading={inviteActionId === `del-${inv.inviteID}`}
                        disabled={inviteActionId !== null}
                        title="Cancel invitation"
                        aria-label={`Cancel invitation for ${inv.createdForEmail}`}
                        onClick={() => {
                          setInviteActionId(`del-${inv.inviteID}`);
                          void (async () => {
                            const orgId = userInfo.organizationID;
                            if (!orgId) return;
                            const ok = await userStore.removeOrganizationInvite(
                              orgId,
                              inv.inviteID,
                            );
                            if (ok) {
                              noticesStore.success('Invitation removed');
                            }
                            setInviteActionId(null);
                          })();
                        }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <div className="mt-10 flex flex-col items-center justify-center gap-4">
        <Text as="h3" variant="studio/body/sm" className="text-tertiary">
          Need help? {!hasEditAccess ? 'Contact your team admin.' : ''}
        </Text>
        {hasEditAccess ? (
          <a
            href="mailto:pros@cartwheel.com?subject=Help%20with%20Team%20Dashboard"
            style={{ textDecoration: 'none' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button icon={<MailIcon />} variant="secondary" size="lg">
              Contact support
            </Button>
          </a>
        ) : (
          <Button
            className="flex w-full max-w-md"
            variant="secondary-destructive"
            size="lg"
            onClick={() => {
              setSelectedMember(userInfo as unknown as OrganizationMember);
              setOpenModal('confirmRemoveMember');
            }}
          >
            Leave team
          </Button>
        )}
      </div>

      {openModal === 'addSeat' && (
        <AddTeamSeatModal close={() => setOpenModal(null)} />
      )}
      {openModal === 'assignTeamSeat' && (
        <AssignOrEditTeamSeatModal
          key={selectedMember?.userID ?? 'invite'}
          close={() => {
            setOpenModal(null);
            setSelectedMember(null);
          }}
          member={selectedMember ?? undefined}
        />
      )}
      {openModal === 'confirmRemoveMember' && selectedMember ? (
        <Modal
          title="Remove team member?"
          open={true}
          onOpenChange={(open) => {
            if (!open) closeConfirmRemoveMember();
          }}
          maxWidth={480}
          footer={
            <div className="flex flex-row justify-between gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={closeConfirmRemoveMember}
                disabled={removingMember}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary-destructive"
                onClick={handleConfirmRemoveMember}
                loading={removingMember}
                disabled={removingMember}
              >
                Remove
              </Button>
            </div>
          }
        >
          <Text variant="studio/body/sm" className="text-tertiary">
            Remove {memberDisplayName(selectedMember)} ({selectedMember.email})
            from the team? They will lose access to Cartwheel.
          </Text>
        </Modal>
      ) : null}
    </div>
  );
}

async function copyMemberUserId(userId: string) {
  try {
    await navigator.clipboard.writeText(userId);
    noticesStore.info('User ID copied');
  } catch {
    noticesStore.error('Could not copy user ID');
  }
}

function TeamRosterCard({
  title,
  subtitle,
  members,
  hasEditAccess,
  ownerUserId,
  currentUserId,
  onEditMember,
  onRequestRemoveMember,
  showSeatActions,
  onAddSeats,
  onManageSeats,
  manageSeatsLoading,
  emptyWorkspaceSeatRows = 0,
  onAssignEmptyWorkspaceSeat,
}: {
  title: string;
  subtitle?: string;
  members: OrganizationMember[];
  hasEditAccess: boolean;
  ownerUserId?: string;
  currentUserId?: string;
  onEditMember: (m: OrganizationMember) => void;
  onRequestRemoveMember?: (m: OrganizationMember) => void;
  showSeatActions?: boolean;
  onAddSeats?: () => void;
  onManageSeats?: () => void;
  manageSeatsLoading?: boolean;
  emptyWorkspaceSeatRows?: number;
  onAssignEmptyWorkspaceSeat?: () => void;
}) {
  const showTableEmptyState =
    members.length === 0 && emptyWorkspaceSeatRows === 0;
  /** Keep mobile to username + actions, then restore the last-active column on md+. */
  const rosterGridCols = hasEditAccess
    ? 'grid-cols-[minmax(0,1fr)_max-content] md:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(160px,max-content)]'
    : 'grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)]';
  return (
    <ProfileSection
      title={title}
      subtitle={subtitle}
      boxed={false}
      actions={
        showSeatActions && onAddSeats ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {onManageSeats ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={manageSeatsLoading}
                onClick={onManageSeats}
              >
                Update plan
              </Button>
            ) : null}
            <Button
              type="button"
              variant="primary"
              size="sm"
              icon={<PlusIcon />}
              onClick={onAddSeats}
            >
              Add
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="border-gray-30 dark:border-gray-60 dark:bg-gray-90 overflow-hidden rounded-xl border bg-white">
        <div
          className={`border-gray-30 dark:border-gray-60 grid gap-3 border-b px-4 py-3 sm:gap-4 sm:px-5 ${rosterGridCols}`}
        >
          <Text
            as="span"
            variant="studio/body/sm"
            className="text-tertiary font-medium"
          >
            Username
          </Text>
          <Text
            as="span"
            variant="studio/body/sm"
            className="text-tertiary hidden font-medium md:inline"
          >
            Last active
          </Text>
          {hasEditAccess ? <span className="sr-only">Actions</span> : null}
        </div>
        {showTableEmptyState ? (
          <EmptyState
            align="start"
            borderStyle="none"
            className="rounded-none px-5 py-8"
            description="No members to display."
          />
        ) : (
          <ul className="divide-gray-30 dark:divide-gray-60 divide-y">
            {members.map((m) => (
              <li key={m.userID}>
                <div
                  className={`grid items-start gap-3 px-4 py-3 sm:gap-4 sm:px-5 md:items-center ${rosterGridCols}`}
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <Avatar
                      userInfo={m as unknown as UserInfo}
                      size={5}
                      className="mt-0.5 shrink-0"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <button
                        type="button"
                        title="Click to copy user ID"
                        className="text-primary focus-visible:ring-primary w-full min-w-0 rounded-sm text-left text-sm font-medium leading-5 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        onClick={() => void copyMemberUserId(m.userID)}
                      >
                        <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0">
                          <span className="min-w-0 truncate">
                            {memberDisplayName(m)}
                          </span>
                          {ownerUserId && m.userID === ownerUserId ? (
                            <span className="text-tertiary shrink-0 font-normal">
                              (Owner)
                            </span>
                          ) : null}
                          {currentUserId && m.userID === currentUserId ? (
                            <span className="text-tertiary shrink-0 font-normal">
                              (You)
                            </span>
                          ) : null}
                        </span>
                      </button>
                      <Text
                        as="span"
                        variant="studio/body/sm"
                        className="text-tertiary truncate text-xs leading-snug opacity-80"
                        title={m.email}
                      >
                        {m.email}
                      </Text>
                      <Text
                        as="span"
                        variant="studio/body/sm"
                        className="text-tertiary text-xs leading-snug opacity-80 md:hidden"
                      >
                        Last active:{' '}
                        {m.lastActiveAt ? (
                          <DaysAgo date={m.lastActiveAt} />
                        ) : (
                          '—'
                        )}
                      </Text>
                    </div>
                  </div>
                  <Text
                    as="span"
                    variant="studio/body/sm"
                    className="text-tertiary hidden md:block"
                    title={m.lastActiveAt || undefined}
                  >
                    {m.lastActiveAt ? <DaysAgo date={m.lastActiveAt} /> : '—'}
                  </Text>
                  {hasEditAccess ? (
                    <div className="flex w-full min-w-0 justify-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        alignIcon="end"
                        icon={<ChevronDownIcon />}
                        onClick={() => onEditMember(m)}
                      >
                        Edit
                      </Button>
                      {onRequestRemoveMember ? (
                        <Button
                          type="button"
                          variant="secondary-destructive"
                          size="sm"
                          icon={<TrashIcon />}
                          title="Remove from team"
                          aria-label={`Remove ${memberDisplayName(m)} from team`}
                          onClick={() => onRequestRemoveMember(m)}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
            {hasEditAccess && onAssignEmptyWorkspaceSeat
              ? Array.from({ length: emptyWorkspaceSeatRows }, (_, i) => (
                  <li key={`empty-seat-${i}`}>
                    <div
                      className={`grid items-start gap-3 px-4 py-3 sm:gap-4 sm:px-5 md:items-center ${rosterGridCols}`}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <HelpIcon />
                        <Text
                          as="span"
                          variant="studio/body/sm"
                          className="text-tertiary"
                        >
                          Empty seat
                        </Text>
                      </div>
                      <Text
                        as="span"
                        variant="studio/body/sm"
                        className="text-tertiary hidden md:block"
                      >
                        —
                      </Text>
                      <div className="flex w-full min-w-0 justify-end">
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={onAssignEmptyWorkspaceSeat}
                        >
                          Assign
                        </Button>
                      </div>
                    </div>
                  </li>
                ))
              : null}
          </ul>
        )}
      </div>
    </ProfileSection>
  );
}
